import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { Redirect } from 'react-router-dom';
import classnames from 'classnames';
import { VelocityTransitionGroup, VelocityComponent } from 'velocity-react';
import { ButtonStyled } from '@nike/nike-design-system-components';
import join from 'url-join';

import { accountDetailTypes } from '../../types/user';
import { selectedDepartmentTypes } from '../../types/shop';
import { FUNDAMENTAL_DATA_TYPE } from '../../fundamentals/types';

import PdpHeader from './PdpHeader';
import PdpPrice from './PdpPrice';
import PdpThumbnails from './PdpThumbnails';
import PdpDeliveryUnitRules from './PdpDeliveryUnitRules';
import PdpFundamentalRules from './PdpFundamentalRules';
import PdpQuantityRules from './PdpQuantityRules';
import { Button, H2, Icon, Link, Table, Spinner } from '../../components';
import BasicSelect from '~COMPONENTS/formInputs/BasicSelect';
import UnitAllotmentProductInfo from '~COMPONENTS/product/UnitAllotmentProductInfo';
import PdpProductTotals from '~COMPONENTS/pdp/PdpProductTotals';
import PurchaseOptionInfo from '~COMPONENTS/product/PurchaseOptionInfo';
import Accordion from '~COMPONENTS/accordion/Accordion';
import GlobalToolTip from '~COMPONENTS/tooltips/GlobalToolTip';
import ProductImageList from '~COMPONENTS/aurora/ProductImageList';
import ToggleButtonGroup from '~COMPONENTS/ToggleButtonGroup';
import { TranslatedCopy } from '../../intl/TranslatedCopy';
import PdpFundamentalPicker from './PdpFundamentalPicker';
import PdpFutureAvailabilities from './PdpFutureAvailabilities';
import PdpProductDetailsModal from './PdpProductDetailsModal';

import { getColumnsByOrg } from './columns';
import futuresColumns from './futuresColumns';

import { closeModal, openModal } from '../../components/modal/actions';
import {
  requestProductDetails,
  requestProductDetailsV2,
  selectProduct,
  requestMinRules,
  setIncludeUnavailableProduct
} from '../actions';
import { ORDER_TYPE_ATONCE, ORDER_TYPE_FUTURES } from '../constants';
import {
  getProductAvailability,
  getProductFutureAvailability,
  getShopProducts,
  getSelectedDepartment,
  getSelectedProduct,
  getProductMinRules,
  getProductMaxRules,
  getProductMaxAmountOrderable
} from '../selectors';
import { accountDetails, getUserData, getUserAccount } from '../../user/selectors';
import { addProducts, EVENT_GROUP_ADD_REMOVE, getAvailableCustomerRequestDates } from '../../carts/actions';
import { orderTypeToCartType , cartIsLoading, shopProductHasAvailability } from '../../carts/constants';

import { calcTotals } from '../../util/calcTotals';
import { mapAvailability } from '../../util/mapAvailability';
import { mergeAllotmentsOrOptions } from '../../util/allotmentsAndOptions';
import { slideDownInLeft, slideUpOutRight } from '../../animation/util';
import {
  getFundamentals,
  getRuleIdWith,
  mapFundamentalDataForStyleColor,
  getRuleWith
} from '../../fundamentals/constants';
import { sortByAvailability, mapAvailabilityToTranslationString } from '../util/helpers';
import { getDefaultSizeConversion } from '../../util/countryFootwearSizing';
import { getPendingStatus } from '../../util/requestReducer';

import { DEFAULT_ONPOPSTATE } from '../../app/constants';


import { getAllotmentsForSelectedDepartment, getCurrentAllotments, getFutureAllotments } from '../../gamedayAccount/selectors';
import { filterCustomerRequestDates } from '../../carts/util/customerRequestDateOptions';
import {
  sendEventToGA,
  GA_EVENT_PAGE_VIEW,
  GA_EVENT_SIZE_CONVERSION,
  GA_EVENT_ADD_FOOTWEAR_TO_CART,
  GA_EVENT_PDP_PAGE_VIEW,
  GA_EVENT_PDP_ADD_TO_CART_CLICK
} from '../../analytics/constants';
import { showToast } from '../../components/toast/actions';

import http from '../../util/http';

const requestProductSizeGuideURL = values => {
  const { country, locale, seasonYear, styleColor } = values;
  const headers = { 'Content-Type': 'text/plain' };
  return http.get(
    join(
      import.meta.env.VITE_SIZE_CHART_URL,
      `?country=${country}`,
      `&locale=${locale}`,
      `&seasonYear=${seasonYear}`,
      `&styleColor=${styleColor}`
    ),
    headers
  );
};

export class Pdp extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedProduct: null,
      sizeGuideURL: '',
      style: null,
      fundamentalSelections: {},
      isFutureAvailabilitiesModalOpen: false,
      isProductDetailsModalOpen: false,
      selectedSizeConversion: null,
      sizeConversionOptions: null,
      customerRequestDatesByProductCode: {},
      availableCustomerRequestDates: [],
      selectedCustomerRequestDate: null
    };

    this.setFundamentalSelection = this.setFundamentalSelection.bind(this);
  }

  componentDidMount() {
    const { accountDetails, selectedDepartment, selectedProduct, unitAllotments } = this.props;

    if (selectedProduct) {
      const productWithOptions = {
        ...selectedProduct,
        products: selectedProduct.products.map(prod => ({
          ...prod,
          purchaseOptions: mergeAllotmentsOrOptions(selectedDepartment.purchaseOptions, prod),
          unitAllotments: mergeAllotmentsOrOptions(unitAllotments, prod)
        }))
      };

      this.setProduct(productWithOptions);
      selectProduct(productWithOptions);
      this.requestProductDetailsModalData(selectedProduct.styleNumber);
    } else if (selectedDepartment && !selectedProduct && accountDetails) {
      this.initializeProduct();
    }

    window.scroll(0, 0);

    sendEventToGA(GA_EVENT_PAGE_VIEW, { pageViewType: GA_EVENT_PDP_PAGE_VIEW });
  }

  componentDidUpdate(prevProps) {
    const { accountDetails } = this.props;

    if (prevProps.accountDetails !== accountDetails) this.initializeProduct();
  }

  componentWillUnmount() {
    window.onpopstate = DEFAULT_ONPOPSTATE;
  }

  setStyle = style => {
    this.setState({ style });
    this.loadCustomerRequestDatesForStyle(style);
  };

  setSelectedProduct = selectedProduct => {
    this.setState({ selectedProduct });
    this.loadProductSizeGuideURL(selectedProduct);
    this.initiateMinMax();
    this.applyCustomerRequestDatesForProduct(selectedProduct?.productCode);
  };

  loadCustomerRequestDatesForStyle = style => {
    const { selectedDepartment, userAccount } = this.props;

    if (!style?.products?.length || selectedDepartment?.type !== ORDER_TYPE_FUTURES) {
      this.setState({
        customerRequestDatesByProductCode: {},
        availableCustomerRequestDates: [],
        selectedCustomerRequestDate: null
      });
      return;
    }

    const productCodes = style.products.map(product => product.productCode);

    getAvailableCustomerRequestDates({
      accountNumber: userAccount.number,
      departmentNumber: selectedDepartment.number,
      cartType: orderTypeToCartType(selectedDepartment.type),
      productCodes
    })
      .then(customerRequestDatesByProductCode => {
        this.setState({ customerRequestDatesByProductCode }, () => {
          const { selectedProduct } = this.state;
          if (selectedProduct?.productCode) {
            this.applyCustomerRequestDatesForProduct(selectedProduct.productCode);
          }
        });
      })
      .catch(() => {
        this.setState({
          customerRequestDatesByProductCode: {},
          availableCustomerRequestDates: [],
          selectedCustomerRequestDate: null
        });
      });
  };

  applyCustomerRequestDatesForProduct = productCode => {
    if (!productCode) {
      this.setState({ availableCustomerRequestDates: [], selectedCustomerRequestDate: null });
      return;
    }

    this.setState(state => {
      const rawDates = state.customerRequestDatesByProductCode[productCode] || [];
      const availableCustomerRequestDates = this.getSelectableCustomerRequestDates(rawDates);

      return {
        availableCustomerRequestDates,
        selectedCustomerRequestDate: availableCustomerRequestDates[0] || null
      };
    });
  };

  getSelectableCustomerRequestDates = (customerRequestDates = []) => {
    const { currentAllotment, futureAllotment, selectedDepartment } = this.props;

    return filterCustomerRequestDates(customerRequestDates, {
      currentAllotment,
      futureAllotment,
      orderType: selectedDepartment?.type
    });
  };

  handleCustomerRequestDateSelect = customerRequestDate => {
    this.setState({ selectedCustomerRequestDate: customerRequestDate });
  };

  formatCustomerRequestDateLabel = crd => {
    const { user } = this.props;
    const locale = user.locale.split('_')[0];

    if (!crd) return '';

    // since date doesn't include time, make the date the same regardless of locale
    const dateObj = new Date(crd);
    const userTimezoneOffset = dateObj.getTimezoneOffset() * 60000;
    const adjustedDateObj = new Date(dateObj.getTime() + userTimezoneOffset);
    return adjustedDateObj.toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' });
  };

  setProduct = styles => {
    const {
      match: { params },
      user
    } = this.props;

    const styleParam = params.productId.split('-')[0];
    const style = styles && (styles.products ? styles : styles.filter(product => product.number === styleParam)[0]);

    if (style) {
      const product = {
        ...style.products.filter(prod => prod.productCode === params.productId)[0],
        styleNumber: style.styleNumber
      };

      this.setStyle(style);
      this.setSelectedProduct(product);

      if (product.division === 'FTWR') {
        const sizeConversionOptions = product.sizes[0].sizeConversions?.map(sc => sc.sizeTypeCode);
        const defaultSizeConversion = user.defaultSizeChart || getDefaultSizeConversion(user.country);
        const selectedSizeConversion = sizeConversionOptions?.includes(defaultSizeConversion)
          ? defaultSizeConversion
          : 'US';
        this.setState({ sizeConversionOptions, selectedSizeConversion });
      }
    }
  };

  setSelectedSizeConversion = selectedSizeConversion => {
    const { user } = this.props;

    this.setState({ selectedSizeConversion });

    sendEventToGA(GA_EVENT_SIZE_CONVERSION, {
      country: user.country,
      defaultSizeConversion: user.defaultSizeChart || getDefaultSizeConversion(user.country),
      selectedSizeConversion
    });
  };

  setFundamentalSelection(graphicOption, textOption = '') {
    const { fundamentalSelections, selectedProduct } = this.state;
    const rule = getRuleWith(this.fundamentalData(), graphicOption, textOption);

    this.setState({
      fundamentalSelections: {
        ...fundamentalSelections,
        [selectedProduct.productCode]: {
          graphicOption: graphicOption,
          textOption: textOption,
          rule
        }
      }
    });
  }

  initializeProduct = () => {
    const { products, setIncludeUnavailableProduct } = this.props;
    const currStyleId = window.location.pathname.split('/').pop().split('-');

    const queryParams = new URLSearchParams(window.location.search);
    // need to set includeUnavailableProduct
    // but also pass it to requestProduct bc state doesn't update before call
    let inclUnavail = false;
    if (queryParams.get('inclUnavail') === '1') {
      inclUnavail = true;
      setIncludeUnavailableProduct(true);
    }

    if (products.length > 0) {
      const currProduct = products.filter(prod => prod.styleNumber === currStyleId)[0];

      if (currProduct) {
        this.normalizeProduct(currProduct);
      } else {
        this.requestProduct(currStyleId[0], inclUnavail);
      }
    } else {
      this.requestProduct(currStyleId[0], inclUnavail);
    }
    this.requestProductDetailsModalData(currStyleId[0]);
  };

  initiateMinMax = () => {
    const { requestMinRules } = this.props;

    if (requestMinRules) requestMinRules().then(() => this.updateMinMaxRules());
  };

  updateMinMaxRules = () => {
    const { selectedProduct } = this.state;

    if (!(selectedProduct && selectedProduct.productCode)) return;

    const { productMinRules, productMaxRules } = this.props;

    // Add min/max to selected product data
    this.setState({
      selectedProduct: {
        ...selectedProduct,
        min: productMinRules && productMinRules[selectedProduct.productCode],
        max: productMaxRules && productMaxRules[selectedProduct.productCode]
      }
    });
  };

  requestProduct = (styleNumber, includeUnavailableProduct) => {
    const {
      accountDetails,
      match: { params },
      requestProductDetails,
      user
    } = this.props;
    requestProductDetails({
      accountNumber: params.accountId,
      deptNumber: params.departmentId,
      includeUnavailableProduct,
      language: user.locale.split('_')[0].toUpperCase(),
      orderType: params.type,
      pricelistType: accountDetails.pricelistType,
      region: accountDetails.region,
      styleNumber
    }).then(({ payload }) => {
      if (payload.styles && payload.styles.length > 0) {
        this.normalizeProduct(payload.styles[0]);
      }
    });
  };

  requestProductDetailsModalData = styleNumber => {
    const {
      match: { params },
      requestProductDetailsV2,
      selectedDepartment,
      user
    } = this.props;

    requestProductDetailsV2({
      accountNumber: params.accountId,
      deptNumber: params.departmentId,
      cartType: selectedDepartment.type,
      language: user.locale.split('_')[0].toUpperCase(),
      styleNumber
    });
  };

  normalizeProduct = style => {
    const { selectedDepartment, selectProduct, unitAllotments } = this.props;
    const styleSorted = sortByAvailability([style])[0];
    const updatedStyle = {
      ...styleSorted,
      products: styleSorted.products.map(prod => ({
        ...prod,
        purchaseOptions: mergeAllotmentsOrOptions(selectedDepartment.purchaseOptions, prod),
        unitAllotments: mergeAllotmentsOrOptions(unitAllotments, prod),
        sizes: prod.sizes.map(size => ({
          promoIndicator: size.promoIndicator,
          sizeCode: size.description,
          displaySize: size.displaySize,
          sizeConversions: size.sizeConversions,
          sortOrderNumber: size.sortOrderNumber,
          upc: size.upc
        }))
      }))
    };

    this.setProduct(updatedStyle);
    selectProduct(updatedStyle);
  };

  loadProductSizeGuideURL = product => {
    const {
      user: { country, locale }
    } = this.props;

    requestProductSizeGuideURL({
      country: country.toLowerCase(),
      locale,
      seasonYear: product.season,
      styleColor: product.productCode
    })
      .then(sizeGuideUrl => {
        this.setState({ sizeGuideURL: sizeGuideUrl || '' });
      })
      .catch(() => {
        this.setState({ sizeGuideURL: '' });
      });
  };

  handleThumbnailClick = id => {
    const {
      match: { params },
      pdpForm,
      selectedDepartment,
      history
    } = this.props;

    if (pdpForm && pdpForm.values) {
      this.handleViewChange(id);
    } else {
      const {
        style: { products }
      } = this.state;
      this.setSelectedProduct(products.filter(product => product.productCode === id)[0]);
      history.push(`/shop/${params.accountId}/dept/${selectedDepartment.number}/${selectedDepartment.type}/${id}`);

      // When user clicks back button in browser, return to gridwall
      window.onpopstate = () => history.goBack();
    }
  };

  handleViewChange = id => {
    const { handleSubmit, openModal } = this.props;

    openModal({
      type: 'alert',
      cancelText: new TranslatedCopy({ id: 'pdp.modal.cancel' }).toString(),
      cancelAction: () => this.handleFormCancel(id),
      confirmText: new TranslatedCopy({ id: 'shop.cart.add' }).toString(),
      confirmAction: handleSubmit(values => {
        this.handleAddToCart(values);
        this.handleFormCancel(id);
      }),
      title: new TranslatedCopy({ id: 'pdp.modal.title' }).toString()
    });
  };

  handleFormCancel = id => {
    const { closeModal, reset } = this.props;

    reset();
    closeModal();
    setTimeout(() => this.handleThumbnailClick(id), 0);
  };

  handleAddToCart = quantity => {
    const { addProducts, reset, fundamentalData, user } = this.props;
    const {
      selectedProduct: currentSelectedProduct,
      selectedSizeConversion,
      selectedCustomerRequestDate,
      availableCustomerRequestDates
    } = this.state;
    const { productCode } = currentSelectedProduct;
    const { graphicOption, textOption } = this.fundamentalSelection();

    const selectedProduct = {
      ...currentSelectedProduct,
      sizes: Object.keys(quantity).reduce((arr, size) => {
        const sizeCode = size.match(/(.*)--(.*)/)[1].replace('_', '.');
        const sizeDisplay =
          currentSelectedProduct.division === 'FTWR'
            ? currentSelectedProduct.sizes
                .find(currSize => currSize.sizeCode === sizeCode)
                ?.sizeConversions?.find(sc => sc.sizeTypeCode === selectedSizeConversion)?.sizeDisplay
            : '';
        const updatedArr = arr;
        updatedArr.push({
          code: sizeCode,
          quantity: quantity[size],
          sizeDisplay
        });
        return updatedArr;
      }, [])
    };

    if (graphicOption && textOption) {
      selectedProduct.fundamentalRuleId = getRuleIdWith(fundamentalData, graphicOption, textOption, productCode);
    }

    if (selectedCustomerRequestDate) {
      selectedProduct.customerRequestDate = selectedCustomerRequestDate;
      selectedProduct.availableCustomerRequestDates = availableCustomerRequestDates;
    }

    addProducts([selectedProduct]);
    reset();

    sendEventToGA(GA_EVENT_PDP_ADD_TO_CART_CLICK);

    if (currentSelectedProduct.division === 'FTWR') {
      sendEventToGA(GA_EVENT_ADD_FOOTWEAR_TO_CART, {
        country: user.country,
        defaultSizeConversion: user.defaultSizeChart || getDefaultSizeConversion(user.country),
        selectedSizeConversion
      });
    }
  };

  renderThumbnails = () => {
    const { style, selectedProduct } = this.state;

    return (
      <PdpThumbnails
        products={style.products}
        currProductCode={selectedProduct.productCode}
        styleNum={style.styleNumber}
        styleName={selectedProduct.copy && selectedProduct.copy.name}
        handleThumbnailClick={this.handleThumbnailClick}
      />
    );
  };

  filterViewCodes = viewCodes => viewCodes.filter(code => !code.includes('WM'));

  /**
   * Returns true if the product code is found within the account's
   * fundamental data AND the selected department type is futures
   * @returns {boolean}
   */
  hasFundamentalData() {
    const { fundamentalData, selectedDepartment } = this.props;
    const { selectedProduct } = this.state;

    return (
      selectedDepartment.type === ORDER_TYPE_FUTURES &&
      (selectedProduct.isFundamental ||
        (fundamentalData.styleColorCodes &&
          Object.keys(fundamentalData.styleColorCodes).includes(selectedProduct.productCode)))
    );
  }

  isValidFundamentalProduct() {
    const { fundamentalData, selectedDepartment, showToast } = this.props;
    const { selectedProduct } = this.state;

    const isFundamentalDataAvailable =
      fundamentalData.styleColorCodes &&
      Object.keys(fundamentalData.styleColorCodes).includes(selectedProduct.productCode);

    const isFundamentalProductError =
      selectedDepartment.type === ORDER_TYPE_FUTURES &&
      (selectedProduct.isFundamental ? !isFundamentalDataAvailable : isFundamentalDataAvailable);

    if (isFundamentalProductError) {
      showToast({
        type: 'error',
        message: new TranslatedCopy({
          id: selectedProduct.isFundamental ? 'pdp.fundamental.rulesError' : 'pdp.fundamental.productError'
        }).toString()
      });
      return false;
    }
    return true;
  }

  fundamentalData() {
    const { fundamentalData } = this.props;
    const { selectedProduct } = this.state;

    return mapFundamentalDataForStyleColor(fundamentalData, selectedProduct.productCode);
  }

  fundamentalSelection() {
    const { fundamentalSelections, selectedProduct } = this.state;

    return fundamentalSelections[selectedProduct.productCode] || {};
  }

  fundamentalSelectionIsValid() {
    if (!this.hasFundamentalData()) return true;
    if (!this.isValidFundamentalProduct()) return false;

    const { graphicOption, textOption } = this.fundamentalSelection();
    return !!graphicOption && !!textOption;
  }

  render() {
    const {
      accountDetails,
      allAvailability,
      futureAvailability,
      handleSubmit,
      pdpForm,
      pristine,
      selectedDepartment,
      submitting,
      valid,
      cartSubmitting,
      user,
      productMaxAmountOrderable,
      isAvailabilityRequestLoading
    } = this.props;
    const {
      selectedProduct,
      sizeGuideURL,
      style,
      isFutureAvailabilitiesModalOpen,
      isProductDetailsModalOpen,
      selectedSizeConversion,
      sizeConversionOptions,
      availableCustomerRequestDates,
      selectedCustomerRequestDate
    } = this.state;

    if (!selectedDepartment) return <Redirect to='/shop/department-select' />;
    if (!(selectedProduct && selectedProduct.prices && selectedProduct.prices[0])) return null;

    const isCustomizable = this.hasFundamentalData();

    const {
      productCode,
      prices,
      copy,
      styleGenderAge,
      division,
      silhouette,
      silhouetteType,
      sport,
      dimensions,
      metaData,
      returnable,
      colorName,
      unitAllotments,
      purchaseOptions,
      rawName,
      sizes,
      availabilityStatus
    } = selectedProduct;

    const isAtOnce = selectedDepartment.type === ORDER_TYPE_ATONCE;
    const currSizeData = mapAvailability(allAvailability, selectedDepartment.type, selectedProduct);
    const currPrice = prices[0];
    const totals = calcTotals(currPrice && currPrice.wholesale, pdpForm && pdpForm.values);
    const { qty: quantity } = totals;
    const currAvailability = allAvailability[productCode];
    const isUnavailable = currAvailability && currAvailability.size === 0;
    const hasFundamentalData = this.hasFundamentalData();
    const fundamentalSelection = this.fundamentalSelection();
    const fundamentalSelectionIsValid = this.fundamentalSelectionIsValid();
    const canAddToCart = valid && fundamentalSelectionIsValid;
    const atOnceColumns = getColumnsByOrg(accountDetails.salesOrgCode);
    const selectedProductFutureAvailability =
      isAtOnce &&
      Object.keys(futureAvailability).length > 0 &&
      futureAvailability[productCode] &&
      Object.keys(futureAvailability[productCode]).length > 0;

    const min =
      selectedProduct.min ||
      (fundamentalSelection && fundamentalSelection.rule && fundamentalSelection.rule.minimumQuantity) ||
      undefined;
    const max = selectedProduct.max || undefined;

    const maxQuantityExceeded = productMaxAmountOrderable && quantity > productMaxAmountOrderable;

    const hasActualAvailability = shopProductHasAvailability(selectedProduct, allAvailability);

    const effectiveAvailabilityStatus = hasActualAvailability ? 'Available' : availabilityStatus;

    const selectedProductIsUnavailable =
      effectiveAvailabilityStatus === 'Available Soon' || effectiveAvailabilityStatus === 'Out of Stock';

    const convertedSizeData = sizeConversionOptions
      ? currSizeData.map(size => {
          const sizeMatch = size.sizeConversions?.find(sc => sc.sizeTypeCode === selectedSizeConversion);
          return {
            ...size,
            displaySize: sizeMatch?.sizeDisplay || ''
          };
        })
      : currSizeData;

    return (
      <>
        <div className='pdp' itemScope itemType='http://schema.org/Product'>
          {style && (
            <>
              <div className='pdp__main'>
                <div className='pdp__images p4-sm'>
                  <ProductImageList
                    filterBy={this.filterViewCodes}
                    imageSets={['PV', 'SV']}
                    limit={1}
                    productCode={selectedProduct.productCode}
                  />
                </div>
                <div className='d-md-h'>{this.renderThumbnails()}</div>
                <div className='d-sm-b d-md-h mb0-md'>
                  <PdpHeader
                    styleName={copy.name}
                    rawName={rawName}
                    selectedProductPrices={currPrice}
                    gender={styleGenderAge}
                    division={division}
                    silhouette={silhouette.description}
                    sport={sport}
                  />
                  <PdpPrice selectedProductPrices={currPrice} />
                </div>
                <div className='pdp__info prl7-sm bg-white'>
                  <div className='pdp__header d-sm-h d-md-b'>
                    {copy && (
                      <PdpHeader
                        styleName={copy.name}
                        rawName={rawName}
                        selectedProductPrices={currPrice}
                        gender={styleGenderAge}
                        division={division}
                        dimensions={dimensions || metaData.dimensions}
                        silhouette={silhouette.description}
                        sport={sport}
                        returnable={returnable}
                      />
                    )}
                  </div>
                  <div className='pdp__info_style body-3 text-color-secondary pt5-sm pb5-sm pb0-md'>
                    <div data-desc='pdp__info_style'>
                      <TranslatedCopy id='global.product.style' />
                      {': '}
                      {productCode}
                    </div>
                    <div data-desc='pdp__info_color'>
                      <TranslatedCopy id='global.color' />
                      {': '}
                      {colorName}
                    </div>
                    <div className='pdp__info_container pt4-sm pb4-sm'>
                      {!returnable && (
                        <div className='text-color-secondary pb3-sm pb0-md' data-desc='pdp__info_non_returnable '>
                          <div className='pdp__non_returnable_icon'>
                            <Icon
                              name='non-returnable'
                              alt={new TranslatedCopy({ id: 'global.nonReturnable' }).toString()}
                            />
                          </div>
                          <div className='pdp__non_returnable_text'>
                            <TranslatedCopy className='pdp__non_returnable_text' id='global.product.nonReturnable' />
                          </div>
                        </div>
                      )}
                      {isCustomizable && (
                        <div className='text-color-secondary pb3-sm pb0-md'>
                          <div className='pdp__non_returnable_icon'>
                            <Icon
                              name='customizable'
                              alt={new TranslatedCopy({ id: 'global.product.fundamental' }).toString()}
                            />
                          </div>
                          <div className='pdp__non_returnable_text'>
                            <TranslatedCopy className='pdp__non_returnable_text' id='global.product.fundamental' />
                          </div>
                        </div>
                      )}
                      {selectedProductIsUnavailable && (
                        <div
                          className={classnames('pdp__unavailable text-color-error', {
                            hasOtherPdpInfo: !returnable || isCustomizable,
                            isOnlyPdpInfo: returnable && !isCustomizable
                          })}
                        >
                          <TranslatedCopy id={mapAvailabilityToTranslationString(availabilityStatus)} />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='d-sm-h d-md-b'>{this.renderThumbnails()}</div>
                  <UnitAllotmentProductInfo unitAllotments={unitAllotments} productName={copy.name} />
                  <PurchaseOptionInfo
                    purchaseOptions={purchaseOptions}
                    silhouette={silhouette}
                    silhouetteType={silhouetteType}
                  />
                  {isUnavailable && (
                    <div
                      className={classnames(
                        'pdp__unavailable_message',
                        'flex-container align-center row bg-gameday-grey-two body-4 pt1-sm pb1-sm prl4-sm mb4-sm'
                      )}
                    >
                      <Icon name='filled-alert' alt='shipping info notification' />
                      <span className='pl2-sm'>
                        <TranslatedCopy id='product.unavailable' />
                      </span>
                    </div>
                  )}

                  {hasFundamentalData && (
                    <div className='pdp__info_size-quantity'>
                      <Accordion
                        id='pdpFundamentalOptions'
                        trigger={
                          <H2 data-desc='pdp__info_fundamental_toggle'>
                            <TranslatedCopy id='pdp.fundamental.graphicOption.title' />
                          </H2>
                        }
                        open
                      >
                        <PdpFundamentalPicker
                          fundamentalRules={this.fundamentalData()}
                          fundamentalSelection={fundamentalSelection}
                          onSelect={this.setFundamentalSelection}
                          productColor={selectedProduct.bom.colors.primary}
                        />
                      </Accordion>
                    </div>
                  )}

                  <div className='pdp__info_size-quantity pb4-md'>
                    <Accordion
                      id='pdpSizes'
                      trigger={
                        <H2 data-desc='pdp__info_sizeQuantity_toggle'>
                          <TranslatedCopy id='pdp.sizeQuantity.title' />
                        </H2>
                      }
                      open
                    >
                      {sizeGuideURL !== '' && (
                        <Link
                          to={sizeGuideURL}
                          extraClassName='body-4 d-sm-b mt-2-sm mb2-sm text-color-secondary'
                          external
                        >
                          <TranslatedCopy id='pdp.sizeQuantity.sizeGuide' />
                        </Link>
                      )}
                      <div data-desc='pdp__info_size-quantity'>
                        {maxQuantityExceeded && (
                          <VelocityComponent animation={{ opacity: 1 }}>
                            <div className='text-color-error'>
                              <VelocityTransitionGroup enter={slideDownInLeft} leave={slideUpOutRight}>
                                <div className='mb2-sm'>
                                  <i className='cart__qtyErrorIcon d-sm-ib body-4 prl2-sm mr2-sm'>!</i>
                                  <TranslatedCopy id='pdp.max.error' values={{ amount: productMaxAmountOrderable }} />
                                </div>
                              </VelocityTransitionGroup>
                            </div>
                          </VelocityComponent>
                        )}
                        {sizeConversionOptions && (
                          <div className='pdp__size-conversion'>
                            <div className='pdp__size-conversion__label'>
                              <TranslatedCopy id='pdp.sizeQuantity.sizeConversion' />
                            </div>
                            <ToggleButtonGroup
                              toggleOptions={sizeConversionOptions}
                              selectButton={this.setSelectedSizeConversion}
                              selectedButton={selectedSizeConversion}
                            />
                          </div>
                        )}
                        <Table
                          overrideClass={classnames('pdp__table', { pdp__table_unavailable: isUnavailable })}
                          data={convertedSizeData}
                          columns={isAtOnce ? atOnceColumns : futuresColumns}
                          cellProps={{
                            selectedProduct,
                            total: quantity,
                            min: min,
                            max: max,
                            isAvailabilityRequestLoading,
                            isFtwr: division === 'FTWR' && !!sizeConversionOptions
                          }}
                        />
                        <PdpDeliveryUnitRules product={selectedProduct} />
                        <PdpQuantityRules maxQuantity={productMaxAmountOrderable} />
                        {hasFundamentalData && <PdpFundamentalRules rule={fundamentalSelection.rule} />}
                        <PdpProductTotals totals={totals} selectedDepartment={selectedDepartment} />
                        {selectedProductFutureAvailability && (
                          <div className='mt6-sm'>
                            <ButtonStyled
                              appearance='textUnderline'
                              type='button'
                              onClick={() => this.setState({ isFutureAvailabilitiesModalOpen: true })}
                            >
                              <TranslatedCopy id='pdp.futureAvailabilities' />
                            </ButtonStyled>
                          </div>
                        )}
                      </div>
                    </Accordion>
                  </div>

                  {selectedDepartment.type === ORDER_TYPE_FUTURES && availableCustomerRequestDates.length > 1 && (
                    <BasicSelect
                      name='pdpCustomerRequestDate'
                      isCart
                      options={availableCustomerRequestDates.map(crd => ({
                        value: crd,
                        label: this.formatCustomerRequestDateLabel(crd)
                      }))}
                      onChange={({ value }) => this.handleCustomerRequestDateSelect(value)}
                      value={selectedCustomerRequestDate}
                      className='mb4-sm'
                      label={new TranslatedCopy({
                        id: 'orderstatus.customerRequestDate'
                      }).toString()}
                    />
                  )}
                  <div className='pdp__submit pb8-sm pt8-sm'>
                    <Button
                      type='submit'
                      onClick={handleSubmit(this.handleAddToCart)}
                      disabled={
                        !canAddToCart || submitting || pristine || maxQuantityExceeded || selectedProductIsUnavailable
                      }
                      data-desc='pdpForm__addToCart'
                      extraClassName='ncss-btn-primary-dark btn-lg'
                    >
                      {(submitting || cartSubmitting) && (
                        <div className='flex-container center'>
                          <Spinner />
                        </div>
                      )}
                      {selectedProductIsUnavailable ? (
                        <TranslatedCopy id={mapAvailabilityToTranslationString(effectiveAvailabilityStatus)} />
                      ) : (
                        <TranslatedCopy id='shop.cart.add' />
                      )}
                    </Button>
                  </div>

                  {copy && copy.description && (
                    <div className='pdp__info_description' data-desc='pdp__info_description_container'>
                      <Accordion
                        id='pdpCopy'
                        trigger={
                          <div className='flex-container row align-center'>
                            <H2>
                              <TranslatedCopy id='pdp.description.title' />
                            </H2>
                            <GlobalToolTip
                              className='ml2-sm'
                              content={new TranslatedCopy({ id: 'pdp.description.tooltip' }).toString()}
                            />
                          </div>
                        }
                        dataDesc='pdp__info_description_toggle'
                        open
                      >
                        <div className='body-3' data-desc='pdp__info_description' itemProp='description'>
                          <p className='text-color-secondary pb8-sm'>{copy.description}</p>
                        </div>
                        <div style={{ paddingBottom: '16px' }}>
                          <ButtonStyled
                            appearance='textUnderline'
                            type='button'
                            onClick={() => this.setState({ isProductDetailsModalOpen: true })}
                          >
                            <TranslatedCopy id='pdp.description.productDetails' />
                          </ButtonStyled>
                        </div>
                      </Accordion>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        {isFutureAvailabilitiesModalOpen && (
          <PdpFutureAvailabilities
            futureAvailabilityData={futureAvailability[productCode]}
            productName={copy.name}
            selectedProductPrices={currPrice}
            productCode={productCode}
            colorName={colorName}
            sizes={sizes}
            locale={user.locale.split('_')[0]}
            isOpen={isFutureAvailabilitiesModalOpen}
            closeModal={() => this.setState({ isFutureAvailabilitiesModalOpen: false })}
          />
        )}
        <PdpProductDetailsModal
          selectedProductPrices={currPrice}
          productCode={productCode}
          isOpen={isProductDetailsModalOpen}
          closeModal={() => this.setState({ isProductDetailsModalOpen: false })}
        />
      </>
    );
  }
}

Pdp.defaultProps = {
  accountDetails: null,
  products: null,
  fundamentalData: {},
  pdpForm: null,
  selectedDepartment: null,
  selectedProduct: null,
  productMaxAmountOrderable: null,
  isAvailabilityRequestLoading: true
};

Pdp.propTypes = {
  accountDetails: accountDetailTypes,
  allAvailability: PropTypes.object.isRequired,
  futureAvailability: PropTypes.object.isRequired,
  addProducts: PropTypes.func.isRequired,
  products: PropTypes.array,
  closeModal: PropTypes.func.isRequired,
  fundamentalData: FUNDAMENTAL_DATA_TYPE,
  handleSubmit: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  isAvailabilityRequestLoading: PropTypes.bool,
  match: PropTypes.object.isRequired,
  openModal: PropTypes.func.isRequired,
  pdpForm: PropTypes.object,
  pristine: PropTypes.bool.isRequired,
  requestProductDetails: PropTypes.func.isRequired,
  requestProductDetailsV2: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  selectedDepartment: selectedDepartmentTypes,
  selectedProduct: PropTypes.object,
  selectProduct: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  userAccount: PropTypes.object.isRequired,
  valid: PropTypes.bool.isRequired,
  productMinRules: PropTypes.object.isRequired,
  productMaxRules: PropTypes.object.isRequired,
  requestMinRules: PropTypes.func.isRequired,
  cartSubmitting: PropTypes.bool.isRequired,
  unitAllotments: PropTypes.array.isRequired,
  productMaxAmountOrderable: PropTypes.number,
  setIncludeUnavailableProduct: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired
};

const mapStateToProps = (state, ownProps) => ({
  accountDetails: accountDetails(state),
  allAvailability: getProductAvailability(state),
  futureAvailability: getProductFutureAvailability(state),
  fundamentalData: getFundamentals(state),
  isAvailabilityRequestLoading: getPendingStatus(state, 'REQUEST_PRODUCT_DETAILS_V2'),
  pdpForm: state.form.pdpForm,
  products: getShopProducts(state),
  selectedDepartment: getSelectedDepartment(state),
  selectedProduct: getSelectedProduct(state),
  user: getUserData(state),
  productMinRules: getProductMinRules(state),
  productMaxRules: getProductMaxRules(state),
  productMaxAmountOrderable: getProductMaxAmountOrderable(state, ownProps?.match?.params?.productId),
  cartSubmitting: cartIsLoading(state, EVENT_GROUP_ADD_REMOVE),
  unitAllotments: getAllotmentsForSelectedDepartment(state).unitAllotments,
  currentAllotment: getCurrentAllotments(state),
  futureAllotment: getFutureAllotments(state, true),
  userAccount: getUserAccount(state)
});

const connectedForm = reduxForm({
  form: 'pdpForm'
})(Pdp);

const connector = connect(mapStateToProps, {
  addProducts,
  closeModal,
  openModal,
  requestProductDetails,
  requestProductDetailsV2,
  selectProduct,
  requestMinRules,
  setIncludeUnavailableProduct,
  showToast
});

export default connector(connectedForm);
